import { useMemo } from 'react'

import type { ChartData } from 'chart.js'
import _round from 'lodash/round'

import SubscriptionIcon from '@/assets/icons/icon-subscriptions.svg'
import MessagesIcon from '@/assets/icons/icons-messages.svg'
import { DoughnutChart } from '@/components/charts/doughnut-chart.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import { CHART_COLORS } from '@/modules/tracking/new-subscriber-analysis/consts/color.ts'
import type { PPVSalesRatioChartType } from '@/modules/tracking/new-subscriber-analysis/types.ts'
import { getPercentValue } from '@/utils/helpers.ts'
import { noop } from '@/utils/misc.ts'

const mapData = (data: Array<PPVSalesRatioChartType>) => {
  const res: ChartData<'doughnut'> = {
    labels: data.map((item) => item.title),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: data.map((item) => item.color),
      },
    ],
  }

  return res
}

interface Props {
  subscribersWhoBought: number
  total: number
}

export const PPVSalesRatioChart = ({ total, subscribersWhoBought }: Props) => {
  const data: Array<PPVSalesRatioChartType> = useMemo(
    () => [
      {
        title: `${subscribersWhoBought} bought`,
        value: _round(subscribersWhoBought, 0),
        color: CHART_COLORS.BLUE,
        icon: <SubscriptionIcon />,
        // onChange is required but in this case we don't need it so just empty function
        onChange: noop,
      },
      {
        title: `${total - subscribersWhoBought} did not buy`,
        value: _round(total - subscribersWhoBought, 0),
        color: CHART_COLORS.LIGHT_BLUE,
        icon: <MessagesIcon />,
        onChange: noop,
      },
    ],
    [subscribersWhoBought, total],
  )

  return (
    <Card className="flex h-full w-full justify-between p-0 shadow-none">
      <CardContent className="align-center flex w-full flex-wrap justify-center gap-12 p-0 sm:flex-nowrap">
        <div className="flex items-center justify-center sm:h-52 sm:w-52">
          {!total ? (
            <div className="h-32 w-32 rounded-full  border-[30px] border-background sm:h-52 sm:w-52 sm:border-[40px]" />
          ) : (
            <DoughnutChart
              data={mapData(data)}
              toggleOptions={{
                hideTooltipBody: true,
                hideLegend: true,
              }}
              showCents={false}
            />
          )}
        </div>
        <div className="flex w-full flex-col justify-center gap-4">
          <div className="flex justify-between">
            <div className="min-w-48">Total New Subscribers</div>
            <div>{total}</div>
          </div>
          <div>
            <div className="flex justify-between">
              <div className="min-w-48  text-sm">Subscribers who bought</div>
              <div>{subscribersWhoBought}</div>
            </div>
            <div className="flex justify-between">
              <div className="min-w-48 text-sm ">
                Subscribers who did not buy
              </div>
              <div>{total - subscribersWhoBought}</div>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="min-w-48">Selling Ratio</div>
            <div>{getPercentValue(subscribersWhoBought / total, 1)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
